import type { HeroBehaviorDefinition } from '../../../content/behaviors/hero-behaviors.js';
import type { Autonomy, BehaviorSettings } from '../../../shared/game/behavior-types.js';

/** Tooltip text for the behavior panel. Each starts with its name, which the tooltip sets as its title. */
const PREFERS: Record<HeroBehaviorDefinition['prefer'], string> = {
  nearest: 'Goes for the nearest foe.',
  weakest: 'Goes for the foe closest to falling.',
  'hardest-hitting': 'Goes for the hardest-hitting foe.',
  'nearest-ally': 'Goes for the foe nearest its ally.',
  engaged: 'Goes for a foe an ally is already fighting.',
};

/** A stance's tooltip, read from its data so every stance explains itself. */
export function stanceHint(stance: HeroBehaviorDefinition): string {
  return [
    stance.name, stance.description,
    stance.reserve === 0 ? 'Spends every action on attacks.' : stance.reserve === 1 ? 'Keeps an action for Defend or Interpose.' : 'Keeps both actions for Interpose and Defend.',
    stance.keepDistance ? 'Favours ranged weapons and backs away from close foes.' : stance.step ? 'In melee, steps up to the next foe nearby.' : 'Only attacks foes already in reach.',
    ...stance.skirmish ? ['Uses its ranged weapon until an ally is fighting, then goes in with its blade.', 'After each blow steps out of reach until it can strike again without Rush.'] : [],
    stance.protectAlly ? 'On Full, stays by its ally.' : stance.step ? 'On Full, goes after any foe in the fight.' : 'On Full, never leaves its spot.',
    ...stance.rescue ? ['On Full, brings potions to Dying allies.'] : [],
    PREFERS[stance.prefer],
  ].join('\n');
}

export const AUTONOMY_HINTS: Record<Autonomy, [string, string]> = {
  manual: ['Manual', 'Manual\nOnly its reactions. Waits for your orders.'],
  attack: ['Attack', 'Attack\nFights foes in reach; in melee steps on to the next one.'],
  abilities: ['Abilities', 'Abilities\nAs Attack, plus class powers, Anticipate Danger and reviving Dying allies in reach.'],
  full: ['Full', 'Full\nAs Abilities, plus moving on its own: goes after any foe in the fight, follows the party\'s focus, walks to Dying allies, and gets out of reach when Dying.'],
};

export const TARGETING_HINTS: Record<BehaviorSettings['targeting'], [string, string]> = {
  closest: ['Closest', 'Closest\nFights the nearest foe first.'],
  'highest-threat': ['Highest threat', 'Highest threat\nFights the highest-level foe first.'],
};

export const QUARRY_HINTS: Record<BehaviorSettings['quarryBonus'], [string, string]> = {
  advantage: ['Advantage', 'Advantage\nAttacks against the Quarry roll with advantage.'],
  damage: ['+LVL damage', '+LVL damage\nAttacks against the Quarry deal extra damage equal to the hero\'s level.'],
};

export const FURY_GUARD_HINTS: Record<BehaviorSettings['furyGuard'], [string, string]> = {
  off: ['Off', 'Off\nFury Dice stay for attacking.'],
  'big-hits': ['Big hits', 'Big hits\nSpends a Fury Die only when the hit soaks all STR+DEX of it, or when the hit would drop the hero.'],
  'every-hit': ['Every hit', 'Every hit\nSpends as many Fury Dice as the hit needs.'],
};

/** Each switch's row and its tooltip. */
export const SWITCH_HINTS: Record<string, string> = {
  'behavior-defend-row': 'Defend\nReaction: spends an action to block a hit with armour, when the hit would drop the hero or armour stops a good part of it.',
  'behavior-opportunity-row': 'Opportunity attacks\nReaction: strikes a foe that walks out of melee reach, with disadvantage.',
  'behavior-interpose-row': 'Interpose\nReaction: leaps in front of a nearby ally and takes the hit for them.',
  'behavior-healing-row': 'Use potions\nDrinks a healing potion when Dying, or once two hits would drop the hero. On Full, also gives them to Dying allies.',
  'behavior-inspiration-row': 'Use Inspiration\nSpends Songweaver\'s Inspiration on allies\' rolls: a miss becomes a hit, a hit a crit.',
  'behavior-relentless-row': 'Use Relentless\nWhen a hit would drop the hero to 0 HP, stands back up at HP equal to its level. Once per Safe Rest.',
  'behavior-ally-row': 'Protect\nThe ally Interpose and Protect Ally look after.',
  'resume-ai': 'Resume AI\nEnds the Stop: the heroes act on their own again.',
};
